import { PARTNER_COCKPIT_MAX_NAME_LENGTH, phoneNumberSchema } from '@houzy/schemas/partner-cockpit/user-management';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateProfileMutation, type PartnerCockpitUser } from '../../../lib/auth';
import { shallowEquals } from '../../../lib/shallowEquals';
import { useDirtyForm } from '../state/useDirtyForm';
import { SettingsCard } from './SettingsCard';

export type PersonalDetailsCardProps = {
    user: Pick<PartnerCockpitUser, 'firstName' | 'lastName' | 'phoneNumber'>;
};

// The form values must be required strings and might differ from the update input shape.
type PersonalDetailsForm = { firstName: string; lastName: string; phoneNumber: string };

const PHONE_PLACEHOLDER = '+41791234567';
const phoneNumberField = phoneNumberSchema();

const toPersonalDetailsForm = (user: PersonalDetailsCardProps['user']): PersonalDetailsForm => ({
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phoneNumber: user.phoneNumber ?? '',
});

export function PersonalDetailsCard({ user }: PersonalDetailsCardProps): ReactElement {
    const { t } = useTranslation();
    const updateProfile = useUpdateProfileMutation();
    const { value, setValue, reset, isDirty } = useDirtyForm<PersonalDetailsForm>(toPersonalDetailsForm(user));

    const update = <K extends keyof PersonalDetailsForm>(key: K, next: PersonalDetailsForm[K]): void => {
        setValue((prev) => ({ ...prev, [key]: next }));
    };

    const phoneInvalid = value.phoneNumber.length > 0 && !phoneNumberField.safeParse(value.phoneNumber).success;

    const handleSave = (): void => {
        const submitted = value;
        updateProfile.mutate(
            {
                firstName: submitted.firstName,
                lastName: submitted.lastName,
                phoneNumber: submitted.phoneNumber,
            },
            {
                onSuccess: (saved) =>
                    setValue((current) => (shallowEquals(current, submitted) ? toPersonalDetailsForm(saved) : current)),
            },
        );
    };

    return (
        <SettingsCard
            title={t('SETTINGS.ACCOUNT.PERSONAL_TITLE')}
            dirty={isDirty}
            saving={updateProfile.isPending}
            disableSave={phoneInvalid}
            onSave={handleSave}
            onReset={reset}>
            <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                        label={t('SETTINGS.ACCOUNT.FIRST_NAME')}
                        fullWidth
                        autoComplete="given-name"
                        slotProps={{ htmlInput: { maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH } }}
                        value={value.firstName}
                        onChange={(e) => update('firstName', e.target.value)}
                    />
                    <TextField
                        label={t('SETTINGS.ACCOUNT.LAST_NAME')}
                        fullWidth
                        autoComplete="family-name"
                        slotProps={{ htmlInput: { maxLength: PARTNER_COCKPIT_MAX_NAME_LENGTH } }}
                        value={value.lastName}
                        onChange={(e) => update('lastName', e.target.value)}
                    />
                </Stack>
                <TextField
                    label={t('SETTINGS.ACCOUNT.PHONE')}
                    fullWidth
                    type="tel"
                    autoComplete="tel"
                    placeholder={PHONE_PLACEHOLDER}
                    value={value.phoneNumber}
                    onChange={(e) => update('phoneNumber', e.target.value)}
                    error={phoneInvalid}
                    helperText={phoneInvalid ? t('SETTINGS.ACCOUNT.PHONE_INVALID') : t('SETTINGS.ACCOUNT.PHONE_HINT')}
                />
            </Stack>
        </SettingsCard>
    );
}
